import { loadOfflineDb, withOfflineDb, getCollection, setCollection, nextId } from './offlineDb';

function normalizePath(url) {
  const u = String(url || '');
  // axios config.url is usually a path like '/classes/'
  const noQuery = u.split('?')[0];
  return noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
}

function pickRoleFromLogin() {
  // Démo offline: SuperAdmin uniquement
  return 'superadmin';
}

function ok(config, data, status = 200, headers = {}) {
  return Promise.resolve({
    data,
    status,
    statusText: status >= 200 && status < 300 ? 'OK' : 'ERROR',
    headers,
    config,
    request: {},
  });
}

function notFound(config, message = 'Not Found') {
  return ok(config, { detail: message }, 404);
}

function parseBody(config) {
  const data = config?.data;
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function extractId(path) {
  const parts = path.split('/').filter(Boolean);
  const last = parts[parts.length - 1];
  const id = Number(last);
  return Number.isFinite(id) ? id : null;
}

function isDetailPath(path, base) {
  // base: '/classes/' -> detail: '/classes/123/'
  if (!path.startsWith(base)) return false;
  const rest = path.slice(base.length);
  return /^\d+\/?$/.test(rest);
}

function listOrPaginated(rows) {
  // some callers accept either array or {results:[]}
  return rows;
}

export function createOfflineAdapter() {
  return async function offlineAdapter(config) {
    const method = String(config?.method || 'get').toLowerCase();
    const path = normalizePath(config?.url);

    // Health / basic endpoints
    if (method === 'get' && path === '/health/') {
      return ok(config, { ok: true, offline: true });
    }

    // Auth endpoints
    if (method === 'post' && path === '/token/') {
      const body = parseBody(config) || {};
      const username = body.username || body.email || 'superadmin';
      const role = pickRoleFromLogin(username);

      withOfflineDb((db) => {
        const users = getCollection(db, 'utilisateurs');
        const existing = users.find((u) => String(u.username).toLowerCase() === String(username).toLowerCase());
        const currentUser = existing || {
          id: nextId(users),
          username,
          email: body.email || `${username}@schoolsense.local`,
          role,
          first_name: 'Compte',
          last_name: 'Démo',
        };
        const nextUsers = existing ? users : [...users, currentUser];
        return {
          ...setCollection(db, 'utilisateurs', nextUsers),
          auth: { ...(db.auth || {}), currentUser },
        };
      });

      return ok(config, {
        access: `offline-access-${Date.now()}`,
        refresh: `offline-refresh-${Date.now()}`,
      });
    }

    if (method === 'post' && path === '/token/refresh/') {
      return ok(config, { access: `offline-access-${Date.now()}` });
    }

    if (method === 'post' && path === '/auth/mfa/totp/login/') {
      // Minimal support: treat as success
      return ok(config, {
        access: `offline-access-${Date.now()}`,
        refresh: `offline-refresh-${Date.now()}`,
      });
    }

    if (method === 'post' && path === '/auth/email/confirm/') {
      return ok(config, { ok: true });
    }

    if (method === 'get' && path === '/me/') {
      const db = loadOfflineDb();
      const currentUser = db?.auth?.currentUser;
      if (currentUser) return ok(config, currentUser);
      // fallback: first superadmin
      const users = getCollection(db, 'utilisateurs');
      const sa = users.find((u) => u.role === 'superadmin') || users[0] || null;
      return ok(config, sa);
    }

    // Domain endpoints used across the app
    if (path === '/classes/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'classes')));
    }

    if (path === '/classes/overview/' && method === 'get') {
      const db = loadOfflineDb();
      const classes = getCollection(db, 'classes');
      return ok(config, classes.map((c) => ({ id: c.id, nom: c.nom, nbEleves: c.nbEleves, niveau: c.niveau })));
    }

    if (path.startsWith('/classes/') && path.endsWith('/eleves/') && method === 'get') {
      const db = loadOfflineDb();
      const classes = getCollection(db, 'classes');
      const parts = path.split('/').filter(Boolean);
      const classeId = Number(parts[1]);
      const classe = classes.find((c) => Number(c.id) === classeId) || null;
      // Demo: generate a few students for the class
      const eleves = Array.from({ length: Math.max(3, Math.min(30, classe?.nbEleves || 10)) }).slice(0, 10).map((_, idx) => ({
        id: idx + 1,
        first_name: `Élève${idx + 1}`,
        last_name: classe?.nom || 'Classe',
        role: 'eleve',
        classe: classe?.id ?? classeId,
      }));
      return ok(config, { classe, count: eleves.length, eleves });
    }

    if (path === '/utilisateurs/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'utilisateurs')));
    }

    if (path === '/cours/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'cours')));
    }

    if (path === '/cours/mine/' && method === 'get') {
      // Demo: just return all courses; UI often filters client-side
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'cours')));
    }

    if (path === '/messages/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'messages')));
    }

    if (path === '/messages/' && method === 'post') {
      const body = parseBody(config) || {};
      const next = withOfflineDb((db) => {
        const rows = getCollection(db, 'messages');
        const created = { id: nextId(rows), box: 'sent', date: new Date().toISOString().slice(0, 10), ...body };
        return setCollection(db, 'messages', [created, ...rows]);
      });
      const rows = getCollection(next, 'messages');
      return ok(config, rows[0]);
    }

    if (path === '/notes/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'notes')));
    }

    if (path === '/devoirs/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'devoirs')));
    }

    if (path === '/paiements/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'paiements')));
    }

    if (path === '/formules/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'formules')));
    }

    if (path === '/logs/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'logs')));
    }

    if (path === '/etablissements/' && method === 'get') {
      const db = loadOfflineDb();
      return ok(config, listOrPaginated(getCollection(db, 'etablissements')));
    }

    // Generic REST-ish handler for collections we seed
    const knownCollections = new Set([
      'classes',
      'utilisateurs',
      'cours',
      'messages',
      'notes',
      'devoirs',
      'paiements',
      'formules',
      'logs',
      'etablissements',
    ]);

    for (const name of knownCollections) {
      const base = `/${name}/`;
      if (path === base && method === 'get') {
        const db = loadOfflineDb();
        return ok(config, listOrPaginated(getCollection(db, name)));
      }
      if (path === base && method === 'post') {
        const body = parseBody(config) || {};
        const next = withOfflineDb((db) => {
          const rows = getCollection(db, name);
          const created = { id: nextId(rows), ...body };
          return setCollection(db, name, [...rows, created]);
        });
        const rows = getCollection(next, name);
        return ok(config, rows[rows.length - 1], 201);
      }
      if (isDetailPath(path, base) && (method === 'patch' || method === 'put')) {
        const id = extractId(path);
        const body = parseBody(config) || {};
        const next = withOfflineDb((db) => {
          const rows = getCollection(db, name);
          const updated = rows.map((r) => (Number(r.id) === id ? { ...r, ...body } : r));
          return setCollection(db, name, updated);
        });
        const rows = getCollection(next, name);
        const found = rows.find((r) => Number(r.id) === id);
        return found ? ok(config, found) : notFound(config);
      }
      if (isDetailPath(path, base) && method === 'delete') {
        const id = extractId(path);
        withOfflineDb((db) => {
          const rows = getCollection(db, name);
          const nextRows = rows.filter((r) => Number(r.id) !== id);
          return setCollection(db, name, nextRows);
        });
        return ok(config, null, 204);
      }
    }

    // School module endpoints: return empty but successful responses to keep UI navigable
    if (path.startsWith('/school/')) {
      if (method === 'get') {
        if (/\/\d+\/$/.test(path)) return ok(config, {});
        return ok(config, []);
      }
      if (method === 'post' || method === 'patch' || method === 'put') {
        return ok(config, { ok: true }, 200);
      }
      if (method === 'delete') {
        return ok(config, null, 204);
      }
    }

    // Default: be permissive for GET to avoid hard crashes in pages not yet mocked
    if (method === 'get') {
      return ok(config, []);
    }

    return notFound(config);
  };
}
